import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { ShieldCheck, Plus, X, Warning, CheckCircle, Clock } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchPolicies,
  fetchPolicy,
  createPolicy,
  updatePolicy,
  publishPolicy,
  retirePolicy,
  POLICY_STATUS_LABEL,
  type PolicyListItem,
  type PolicyStatus,
} from '@/lib/policiesApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

function StatusBadge({ status, needsReview }: { status: PolicyStatus; needsReview?: boolean }) {
  const cls: Record<PolicyStatus, string> = {
    draft: 'bg-muted text-muted-foreground',
    under_review: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    published: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
    retired: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}>{POLICY_STATUS_LABEL[status]}</span>
      {needsReview && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
          <Clock className="h-3 w-3" /> Needs review
        </span>
      )}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

export default function PoliciesPage() {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<PolicyListItem | 'new' | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const policiesQ = useQuery({ queryKey: ['policies'], queryFn: fetchPolicies });
  const policies = policiesQ.data ?? [];

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Policies"
        subtitle="Publish chambers policies and evidence that every member has accepted them."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            New policy
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {policiesQ.isLoading ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Loading policies…</div>
          ) : policies.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No policies yet. Create one, then publish it for members to accept.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3">Policy</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Accepted</th>
                  <th className="px-5 py-3">Review</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id} onClick={() => setOpenId(p.id)} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/40">
                    <td className="px-5 py-3">
                      <div className="font-medium text-foreground">{p.title}</div>
                      {p.category && <div className="text-xs text-muted-foreground">{p.category}</div>}
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={p.status} needsReview={p.needsReview} /></td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {p.compliance ? `${p.compliance.accepted} / ${p.compliance.total}` : '—'}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{fmtDate(p.reviewDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <PolicyEditor
          policyId={editing === 'new' ? null : editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => { policiesQ.refetch(); setEditing(null); }}
        />
      )}
      {openId && (
        <PolicyDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => policiesQ.refetch()}
          onEdit={() => { const p = policies.find((x) => x.id === openId); if (p) setEditing(p); setOpenId(null); }}
        />
      )}
    </div>
  );
}

function PolicyEditor({ policyId, onClose, onSaved }: { policyId: string | null; onClose: () => void; onSaved: () => void }) {
  const detailQ = useQuery({ queryKey: ['policy', policyId], queryFn: () => fetchPolicy(policyId!), enabled: !!policyId });
  const existing = detailQ.data?.policy;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (existing && !seeded) {
    setTitle(existing.title);
    setCategory(existing.category ?? '');
    setBodyHtml(existing.bodyHtml);
    setReviewDate(existing.reviewDate ? existing.reviewDate.slice(0, 10) : '');
    setSeeded(true);
  }

  const save = async () => {
    if (title.trim().length < 2) { setError('Title must be at least 2 characters'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        title: title.trim(),
        category: category.trim() || undefined,
        bodyHtml,
        reviewDate: reviewDate ? new Date(reviewDate).toISOString() : undefined,
      };
      if (policyId) await updatePolicy(policyId, { ...payload, category: category.trim() || null, reviewDate: reviewDate ? new Date(reviewDate).toISOString() : null });
      else await createPolicy(payload);
      toast.success(policyId ? 'Policy saved' : 'Policy created');
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the policy'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center" onClick={onClose}>
      <ScrollLock />
      <div className="my-8 w-full max-w-2xl rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="text-sm font-semibold text-foreground">{policyId ? 'Edit policy' : 'New policy'}</span>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Equality & Diversity Policy" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Category (optional)</span>
              <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Equality, GDPR, Conduct" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Review date (optional) — flags for review when it passes</span>
            <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Policy content (HTML — tables, links and formatting supported)</span>
            <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={12} placeholder="<h2>Purpose</h2><p>…</p>" className="w-full rounded-lg border border-border bg-card p-3 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : policyId ? 'Save' : 'Create'}</Button>
        </div>
      </div>
    </div>
  );
}

function PolicyDrawer({ id, onClose, onChanged, onEdit }: { id: string; onClose: () => void; onChanged: () => void; onEdit: () => void }) {
  const qc = useQueryClient();
  const detailQ = useQuery({ queryKey: ['policy', id], queryFn: () => fetchPolicy(id) });
  const d = detailQ.data;

  const publishMut = useMutation({
    mutationFn: () => publishPolicy(id),
    onSuccess: (r) => { toast.success(`Published — ${r.acknowledgementsRequested} member${r.acknowledgementsRequested === 1 ? '' : 's'} asked to accept`); qc.invalidateQueries({ queryKey: ['policy', id] }); onChanged(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not publish')),
  });
  const retireMut = useMutation({
    mutationFn: () => retirePolicy(id),
    onSuccess: () => { toast.success('Policy retired'); qc.invalidateQueries({ queryKey: ['policy', id] }); onChanged(); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not retire')),
  });

  const p = d?.policy;
  const isRetired = p?.status === 'retired';
  const accepted = d?.acknowledgements.filter((a) => a.state === 'accepted') ?? [];
  const pending = d?.acknowledgements.filter((a) => a.state !== 'accepted') ?? [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <ScrollLock />
      <div className="h-full w-full max-w-lg overflow-y-auto border-l border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" /><span className="text-sm font-semibold text-foreground">Policy</span></div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {!p ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 p-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">{p.title}</span>
                <StatusBadge status={p.status} />
              </div>
              {p.category && <div className="text-xs text-muted-foreground">{p.category}</div>}
              <div className="mt-1 text-xs text-muted-foreground">
                {p.version > 0 ? `Version ${p.version}` : 'Not yet published'}
                {p.reviewDate && ` · review ${fmtDate(p.reviewDate)}`}
              </div>
            </div>

            {!isRetired && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
                <Button variant="primary" size="sm" onClick={() => publishMut.mutate()} disabled={publishMut.isPending}>
                  {p.status === 'published' ? 'Publish update' : 'Publish'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => retireMut.mutate()} disabled={retireMut.isPending}>Retire</Button>
              </div>
            )}

            {/* Compliance */}
            {p.status === 'published' && (
              <div className="rounded-lg border border-border p-4">
                <div className="mb-2 text-xs font-medium text-muted-foreground">Acceptance — version {p.version}</div>
                <div className="mb-3 text-sm text-foreground">{accepted.length} of {d.acknowledgements.length} accepted</div>
                <div className="space-y-1.5">
                  {accepted.map((a) => (
                    <div key={a.user.id} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle className="h-4 w-4 text-emerald-500" /> {a.user.firstName} {a.user.lastName}
                    </div>
                  ))}
                  {pending.map((a) => (
                    <div key={a.user.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" /> {a.user.firstName} {a.user.lastName} <span className="text-xs">· pending</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content preview (sanitised) */}
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">Content</div>
              <div
                className="prose prose-sm max-w-none rounded-lg border border-border p-4 text-sm text-foreground dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.bodyHtml || '<p class="text-muted-foreground">No content yet.</p>') }}
              />
            </div>

            {isRetired && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This policy is retired and read-only.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
